import React, { useEffect, useState, forwardRef } from 'react';
import PropTypes from 'prop-types';
import {
  Slide,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Grid,
  Box,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import validationRules from '../../utils/validations.util';
import Iconify from '../common/iconify.component';
import Loading from '../table-elements/loading.component';
import SnackbarInfo from '../common/snackbar-info.component';
import {
  createNewData,
  updateDataById,
  viewDataById,
} from '../../services/subscriptions.service';
import Status from '../form-elements/status.component';
import Categories from '../form-elements/categories.component';
import TransmissionOrgan from '../form-elements/transmission-organ.component';
import Subscribers from '../form-elements/subscribers.component';
import {
  CONTENT_ALIAS,
  CONTENT_VIDEOS_SKU,
  MULTIPLE_CAT_PER_VIDEO,
} from '../../config/const.config';

const Transition = forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));

const AddEditSubscription = ({ title, user, dataId, onCancel, onSuccess }) => {
  const {
    control,
    formState: { errors },
    watch,
    handleSubmit,
    setValue,
  } = useForm({
    defaultValues: {
      email: '',
      category: null,
      transmissionOrganID: null,
      status: null,
    },
  });

  const [loading, setLoading] = useState(true);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });
  const [programId, setProgramId] = useState(0);
  const programWatch = watch('category');

  const saveNewData = (payload) => {
    createNewData(payload)
      .then(() => {
        onSuccess('New Subscription added successfully.');
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setFormSubmitted(false);
      });
  };

  const updateExistingData = (payload) => {
    updateDataById(dataId, payload)
      .then(() => {
        onSuccess('Subscription details updated successfully.');
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setFormSubmitted(false);
      });
  };

  const onFormSubmit = (data) => {
    setFormSubmitted(true);
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    const categoriesIdStrArr = [];
    if (!MULTIPLE_CAT_PER_VIDEO) {
      categoriesIdStrArr.push(data.category);
    } else if (MULTIPLE_CAT_PER_VIDEO) {
      if (typeof data.categories === 'object') {
        data.categories.forEach((item) => {
          const spiltData = item.split('] -');
          const categoryId = spiltData[0].slice(1);

          categoriesIdStrArr.push(`${categoryId}`);
        });
      }
    }

    const payload = {
      email: data.email.email || '',
      category: categoriesIdStrArr || [],
      transmissionOrganID: data.transmissionOrganID || 0,
      status: data.status,
    };
    if (dataId) {
      updateExistingData(payload);
    } else {
      saveNewData(payload);
    }
  };

  useEffect(() => {
    setProgramId(programWatch);
  }, [programWatch]);

  useEffect(() => {
    if (!dataId) {
      setValue('category', 1);
      setLoading(false);
      return;
    }

    viewDataById(dataId)
      .then((res) => {
        // Fill form values
        const autofillEmail = {
          id: dataId,
          firstName: res.data.firstName || '',
          lastName: res.data.lastName || '',
          email: res.data.email || '',
          contact: res.data.contact || '',
          profilePicture: res.data.profilePic || '',
        };
        setValue('email', autofillEmail || {});
        setValue('category', res.data?.programId || null);
        setValue('transmissionOrganID', res.data?.transmissionOrganId || 0);
        setValue('status', res.data?.status || 0);

        setProgramId(res.data?.programId || 0);
        setLoading(false);
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setLoading(false);
      });
  }, [dataId]);

  return (
    <Dialog
      open
      aria-labelledby="add-dialog-title"
      aria-describedby="add-dialog-description"
      TransitionComponent={Transition}
      fullWidth
      maxWidth="lg"
    >
      <DialogTitle
        id="view-dialog-title"
        sx={{ display: 'flex', alignItems: 'center' }}
      >
        {title}
        {!!dataId && user.profilePic && (
          <Box
            component="img"
            sx={{
              height: 30,
              width: 30,
              objectFit: 'cover',
              borderRadius: 15,
              display: 'inline',
              marginX: 1,
            }}
            src={user.profilePic}
          />
        )}
        {!!dataId && user.email}
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onCancel}
        size="small"
        sx={{
          position: 'absolute',
          right: 10,
          top: 15,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <Iconify icon="ic:outline-close" />
      </IconButton>

      <DialogContent dividers id="add-dialog-description">
        {loading && <Loading />}

        {!loading && (
          <form id="add-edit-form" onSubmit={handleSubmit(onFormSubmit)}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="email"
                  control={control}
                  rules={validationRules.REQUIRED}
                  render={({ field: { onChange, value } }) => (
                    <Subscribers
                      id="email"
                      name="email"
                      label="Enter Subscriber Email"
                      defaultValue={value}
                      onChange={onChange}
                      sx={{ width: '100%' }}
                      error={errors?.email?.message || ''}
                      disabled={dataId}
                    />
                  )}
                />
              </Grid>

              {dataId === 0 && (
                <Grid item xs={12} sm={6} md={6}>
                  <Controller
                    name="status"
                    control={control}
                    rules={validationRules.REQUIRED}
                    render={({ field: { onChange, value } }) => (
                      <Status
                        id="sub_status"
                        name="sub_status"
                        label="Status"
                        defaultValue={value}
                        onChange={onChange}
                        validOptions={[0, 1]}
                        sx={{ width: '100%' }}
                        error={errors?.status?.message || ''}
                      />
                    )}
                  />
                </Grid>
              )}
              {dataId !== 0 && (
                <Grid item xs={12} sm={6} md={6}>
                  <Controller
                    name="status"
                    control={control}
                    rules={validationRules.REQUIRED}
                    render={({ field: { onChange, value } }) => (
                      <Status
                        id="sub_status"
                        name="sub_status"
                        label="Status"
                        defaultValue={value}
                        onChange={onChange}
                        validOptions={[0, 1, 2]}
                        sx={{ width: '100%' }}
                        error={errors?.status?.message || ''}
                      />
                    )}
                  />
                </Grid>
              )}

              {!MULTIPLE_CAT_PER_VIDEO && (
                <Grid item xs={12} sm={6} md={6} sx={{ display: 'none' }}>
                  <Controller
                    name="category"
                    control={control}
                    rules={validationRules.REQUIRED}
                    render={({ field: { onChange, value } }) => (
                      <Categories
                        id="category"
                        name="category"
                        label={`Select ${CONTENT_ALIAS.CATEGORY_SINGULAR}`}
                        defaultValue={value}
                        onChange={onChange}
                        sx={{ width: '100%' }}
                        error={errors?.category?.message || ''}
                        disabled={dataId}
                      />
                    )}
                  />
                </Grid>
              )}

              <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="transmissionOrganID"
                  control={control}
                  rules={validationRules.REQUIRED}
                  render={({ field: { onChange, value } }) => (
                    <TransmissionOrgan
                      id="transmissionOrganID"
                      name="transmissionOrganID"
                      label={`Select ${CONTENT_VIDEOS_SKU.ALIAS_SINGULAR}`}
                      defaultValue={value}
                      onChange={onChange}
                      validOptions={[0, 1, 2, 3]}
                      sx={{ width: '100%' }}
                      error={errors?.transmissionOrganID?.message || ''}
                      programId={Number(programId)}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </form>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'left', marginLeft: 2 }}>
        <Button
          type="submit"
          form="add-edit-form"
          color="primary"
          variant="contained"
          disabled={loading || formSubmitted}
        >
          Save
        </Button>
        <Button
          color="secondary"
          variant="contained"
          onClick={onCancel}
          disabled={formSubmitted}
        >
          Cancel
        </Button>
      </DialogActions>

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </Dialog>
  );
};

AddEditSubscription.propTypes = {
  title: PropTypes.string.isRequired,
  user: PropTypes.object,
  dataId: PropTypes.number.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};
AddEditSubscription.defaultProps = {
  user: {},
};

export default AddEditSubscription;
